import React from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, makeStyles } from '@material-ui/core';
import IncidentPdfDocument from './IncidentPdfDocument';

const useStyles = makeStyles((theme) => ({
  content: {
    padding: theme.spacing(1),
    minWidth: 800,
    height: '80vh',
    display: 'flex',
    flexDirection: 'column'
  },
  viewer: {
    width: '100%',
    height: '100%',
    border: 'none',
  }
}));

const NewPrintDialog = ({ open, onClose, incident, draftIncident, logoUrl }) => {
  const classes = useStyles();
  const [mergedPdfUrl, setMergedPdfUrl] = React.useState(null);
  const [isMerging, setIsMerging] = React.useState(false);

  React.useEffect(() => {
    if (!open || !incident) return;
    
    let isCancelled = false;
    let currentUrl = null;

    const generatePdf = async () => {
      try {
        setIsMerging(true);
        setMergedPdfUrl(null);
        
        const newTextContents = {};
        const pdfUrlsToFetch = [];

        // 1. Fetch external string layouts and gather PDF links
        if (incident.attachments) {
          const fetchPromises = incident.attachments.map(async (a) => {
            const isText = a.fileType === 'text/plain' || (a.fileName && a.fileName.toLowerCase().endsWith('.txt'));
            const isPdf = a.fileType === 'application/pdf' || (a.fileName && a.fileName.toLowerCase().endsWith('.pdf'));
            const isOffice = a.fileName && /\.(doc|docx|xls|xlsx|ppt|pptx|rtf)$/i.test(a.fileName.toLowerCase());

            if (isText && a.downloadUrl) {
              try {
                const res = await fetch(a.downloadUrl);
                const text = await res.text();
                newTextContents[a.id || a.fileName || a.name] = text;
              } catch (e) {
                console.error('Failed fetching text', e);
              }
            } else if (isPdf && a.downloadUrl) {
              pdfUrlsToFetch.push({ url: a.downloadUrl, name: a.name });
            } else if (isOffice && a.downloadUrl) {
              try {
                // Fetch original document byte stream
                const docRes = await fetch(a.downloadUrl);
                const docBlob = await docRes.blob();
                
                // Mount stream directly onto a FormData payload
                const formData = new FormData();
                formData.append('files', docBlob, a.fileName || a.name || 'document.docx');
                
                // POST payload to your Gotenberg LibreOffice endpoint
                const gotenbergRes = await fetch('https://gotenberg.incidentxpress.com/forms/libreoffice/convert', {
                  method: 'POST',
                  body: formData
                });
                
                if (gotenbergRes.ok) {
                  const pdfBlob = await gotenbergRes.blob();
                  const buffer = await pdfBlob.arrayBuffer();
                  // Directly supply the completed format buffer 
                  pdfUrlsToFetch.push({ buffer: buffer, name: a.name });
                } else {
                  console.error('Gotenberg rejected the conversion request:', a.name, gotenbergRes.statusText);
                }
              } catch (e) {
                console.error('Gotenberg POST pipeline failed', e);
              }
            }
          });
          await Promise.all(fetchPromises);
        }

        if (isCancelled) return;

        // 2. Generate Base Incident Document via @react-pdf/renderer
        const { pdf } = await import('@react-pdf/renderer');
        const doc = <IncidentPdfDocument incident={incident} draftIncident={draftIncident} logoUrl={logoUrl} textContents={newTextContents} loadingTexts={false} />;
        const basePdfBlob = await pdf(doc).toBlob();
        const basePdfBytes = await basePdfBlob.arrayBuffer();

        if (isCancelled) return;

        // 3. Post-process the PDF with pdf-lib to merge attachments and stamp global footers
        let finalPdfBytes;
        const { PDFDocument, rgb, StandardFonts } = await import('pdf-lib');
        
        const mergedPdf = await PDFDocument.create();
        const baseDoc = await PDFDocument.load(basePdfBytes);
        const basePages = await mergedPdf.copyPages(baseDoc, baseDoc.getPageIndices());
        basePages.forEach(p => mergedPdf.addPage(p));

        const helveticaFont = await mergedPdf.embedFont(StandardFonts.HelveticaBold);
        const helveticaNormalFont = await mergedPdf.embedFont(StandardFonts.Helvetica);

        // Merge external PDF attachments if any
        for (const pdfItem of pdfUrlsToFetch) {
          try {
            let attBytes;
            if (pdfItem.buffer) {
              attBytes = pdfItem.buffer;
            } else {
              const res = await fetch(pdfItem.url);
              attBytes = await res.arrayBuffer();
            }
            const attDoc = await PDFDocument.load(attBytes);
            const attPages = await mergedPdf.copyPages(attDoc, attDoc.getPageIndices());
            
            // Stamp the header onto each page of the attached PDF natively
            attPages.forEach((p) => {
              const { height } = p.getSize();
              p.drawText(`Incident #${incident?.number || ''} Attachment: ${pdfItem.name}`, {
                x: 30,
                y: height - 25,
                size: 10,
                font: helveticaFont,
                color: rgb(0.4, 0.4, 0.4),
              });
              mergedPdf.addPage(p);
            });
          } catch (e) {
            console.error('Failed fetching or merging pdf attachment', e);
          }
        }

        // Apply a global sequence footer to every single page identically!
        const allPages = mergedPdf.getPages();
        const totalPages = allPages.length;
        allPages.forEach((page, index) => {
          const { width } = page.getSize();
          
          // Draw standard divider line securely inside underneath bottom-margin
          page.drawLine({
            start: { x: 30, y: 25 },
            end: { x: width - 30, y: 25 },
            thickness: 1,
            color: rgb(0.85, 0.85, 0.85),
          });

          // Draw Incident Footer string securely fixed to the left bounds
          page.drawText(`Incident #${incident?.number || ''}`, {
            x: 30,
            y: 12,
            size: 9,
            font: helveticaNormalFont,
            color: rgb(0.5, 0.5, 0.5),
          });

          // Measure textual character span and Draw Pagination sequence tied strictly to right bounds
          const paginationText = `Page ${index + 1} of ${totalPages}`;
          const textWidth = helveticaNormalFont.widthOfTextAtSize(paginationText, 9);
          page.drawText(paginationText, {
            x: width - 30 - textWidth,
            y: 12,
            size: 9,
            font: helveticaNormalFont,
            color: rgb(0.5, 0.5, 0.5),
          });
        });

        // Set the native PDF metadata so browser viewers display a clean title instead of the blob UUID
        const safeIncidentNumber = incident?.number || 'Draft';
        mergedPdf.setTitle(`Incident Report #${safeIncidentNumber}`);
        mergedPdf.setAuthor('Incident Xpress');
        mergedPdf.setSubject(`Confidential Incident Report for Incident #${safeIncidentNumber}`);

        finalPdfBytes = await mergedPdf.save();

        if (isCancelled) return;

        // 4. Create Object URL natively for iframe display
        const finalBlob = new Blob([finalPdfBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(finalBlob);
        currentUrl = url;
        setMergedPdfUrl(url);

      } catch (err) {
        console.error('Failed merging PDFs', err);
      } finally {
        if (!isCancelled) {
          setIsMerging(false);
        }
      }
    };

    generatePdf();

    return () => {
      isCancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [open, incident, logoUrl]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Print Incident Report</DialogTitle>
      <DialogContent className={classes.content}>
        {isMerging || !mergedPdfUrl ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
            <h2 style={{ color: '#555' }}>Generating Document...</h2>
            <p style={{ color: '#888' }}>This may take a moment if merging large attachments.</p>
          </div>
        ) : (
          <iframe 
            src={`${mergedPdfUrl}#toolbar=1&navpanes=0&scrollbar=1`} 
            className={classes.viewer} 
            title="Incident Report" 
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Close
        </Button>
        <Button 
          color="primary" 
          variant="contained" 
          disabled={isMerging || !mergedPdfUrl}
          href={mergedPdfUrl}
          download={`incident-${incident?.number || 'report'}.pdf`}
        >
          {isMerging ? 'Preparing Document...' : 'Download PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

NewPrintDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  incident: PropTypes.object,
  draftIncident: PropTypes.object,
  logoUrl: PropTypes.string,
};

export default NewPrintDialog;
